import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ArrowRight, CheckCircle2, X as XIcon } from "lucide-react";
import api from "@/lib/api";

/**
 * Compact, link-only status card for Brand Learning.
 *
 * Displays one of three states:
 *   • "Not set up"            — no examples and no style profile
 *   • "X examples added"      — has examples but hasn't analyzed yet
 *   • "Style profile active"  — analysis done and active
 *
 * Used on Dashboard, Generate Post, and Autopilot pages to surface the
 * feature without forcing a separate setup step. Always links to
 * /brand-learning.
 *
 * Props:
 *   • compact: bool   — slimmer variant for tight sidebars (default false)
 *   • testid:  string — root data-testid (default 'brand-learning-status-card')
 */
export default function BrandLearningStatusCard({ compact = false, testid = "brand-learning-status-card", onDismiss }) {
  const [state, setState] = useState({
    loading: true,
    examples: 0,
    usableCount: 0,
    minForAnalysis: 3,
    active: false,
    hasProfile: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ex, sp] = await Promise.all([
          api.get("/brand-learning/examples").catch(() => null),
          api.get("/brand-learning/style-profile").catch(() => null),
        ]);
        if (cancelled) return;
        setState({
          loading: false,
          examples: ex?.data?.count || 0,
          usableCount: ex?.data?.usable_count || 0,
          minForAnalysis: ex?.data?.min_for_analysis || 3,
          active: !!sp?.data?.active,
          hasProfile: !!sp?.data?.profile,
        });
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Four canonical states (must match BrandLearning page status pill):
  //   • not_started: 0 examples
  //   • in_progress: 1–2 usable examples
  //   • ready:       3+ usable examples, no analysis yet
  //   • active:      Style Profile analyzed and active
  let state_id;
  let title;
  let subtitle;
  let badge;
  let badgeTone = "neutral";

  if (state.loading) {
    state_id = "loading";
    title = "Brand Learning";
    subtitle = "Loading…";
    badge = "—";
  } else if (state.active && state.hasProfile) {
    state_id = "active";
    title = "Brand Learning: Active";
    subtitle = "Brand Style Profile is active and will guide future content.";
    badge = "Active";
    badgeTone = "ok";
  } else if (state.usableCount >= state.minForAnalysis) {
    state_id = "ready";
    title = "Brand Learning: Ready to analyze";
    subtitle = "You have enough examples. Run brand analysis to create your saved voice.";
    badge = "Ready";
    badgeTone = "warn";
  } else if (state.examples > 0 || state.usableCount > 0) {
    state_id = "in_progress";
    title = "Brand Learning: In progress";
    subtitle = `Add at least ${state.minForAnalysis} examples to analyze your brand style.`;
    badge = `${state.usableCount}/${state.minForAnalysis} usable`;
    badgeTone = "warn";
  } else {
    state_id = "not_started";
    title = "Brand Learning: Not started";
    // Sep-2026 UX nudge: subtle recommendation for users who already have
    // an established social presence. Copy is educational, not blocking;
    // the whole card is already a Link to /brand-learning so the arrow
    // acts as the CTA.
    subtitle =
      "Already posting on social? Teach Postmoro your existing style for more on-brand generations.";
    badge = "Not started";
  }

  const badgeClass =
    badgeTone === "ok"
      ? "bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)] border-[color:rgba(27,168,159,0.35)]"
      : badgeTone === "warn"
      ? "bg-[color:rgba(255,176,32,0.08)] text-[color:var(--warning)] border-[color:rgba(255,176,32,0.30)]"
      : "bg-[color:var(--surface-2)] text-[color:var(--text-3)] border-[color:var(--border-subtle)]";

  return (
    <div className="relative">
      <Link
        to="/brand-learning"
        data-testid={testid}
        data-state={state_id}
        className={
          "group block rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]/60 hover:bg-[color:var(--surface-3)] transition px-4 " +
          (compact ? "py-2.5" : "py-3") +
          (typeof onDismiss === "function" && !state.active ? " pr-10" : "")
        }
      >
        <div className="flex items-center gap-3">
          <div className={
            "flex items-center justify-center rounded-md shrink-0 " +
            (compact ? "h-8 w-8" : "h-9 w-9") +
            " " +
            (state.active
              ? "bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)]"
              : "bg-[color:var(--surface-3)] text-[color:var(--text-3)]")
          }>
            {state.active ? <CheckCircle2 className="h-4 w-4" /> : <BookOpen className="h-4 w-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <div className={"font-medium text-[color:var(--text-1)] " + (compact ? "text-xs" : "text-sm")}>
                {title}
              </div>
              <span
                data-testid={testid + "-badge"}
                className={
                  "text-[10px] font-medium px-1.5 py-0.5 rounded-full border " + badgeClass
                }
              >
                {badge}
              </span>
            </div>
            <div className={"text-[color:var(--text-3)] mt-0.5 " + (compact ? "text-[11px]" : "text-xs")}>
              {subtitle}
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-[color:var(--text-3)] group-hover:text-[color:var(--text-1)] transition shrink-0" />
        </div>
      </Link>
      {typeof onDismiss === "function" && !state.active ? (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDismiss();
          }}
          aria-label="Dismiss Brand Learning card"
          title="Not for me"
          data-testid={testid + "-dismiss"}
          className="absolute top-1.5 right-1.5 h-6 w-6 rounded-md flex items-center justify-center text-[color:var(--text-3)] hover:text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)] transition"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </div>
  );
}
